// Home dashboard right-click context menus. The empty-area menu
// (pages/main.js) is [Refresh, Customize dashboard]. The per-widget menu
// (components/HomeWidgetFrame.js) is Refresh, [Change with AI…],
// Add/Change color, [Widget size — touch only], Hide "<title>",
// [Share to workspace / Make personal — an AI-made widget the member may
// edit], Customize dashboard, and LAST [Delete "<title>" — an AI-made widget
// the member may delete] (CRUD standard rule 7: Delete is the last item). No
// menu offers Autolayout now; the dispatcher's `autolayout` /
// `undoAutolayout` branches below remain from it. Both menus open the shared
// house ContextDropdown (`root.contextDropdown`) with
// `actionFn: 'homeContextAction'` — the EXACT shape the account menu + /files
// context menu use (MenuPanel / MenuPanelItem).
//
// The data-heavy operations (refetch every widget's data, hide a specific
// widget gap-free) live on the home page (pages/main.js) where the loaders +
// Gridstack are, and are exposed to this dispatcher through `root._homeOps` — a
// hook bag the page stashes on mount, mirroring how it already stashes
// `el.__homeGrid` / `gridEl.__homeApplyMode` for cross-tree access. `customize`
// needs no page state, so it flips `_homeCustomizeMode` directly (identical to
// shared/functions/accountMenu.js's "Customize dashboard" action).
//
// `this` is the clicked ContextDropdownItem element (el.call binds it), so
// `this.state.root` is the app root — same contract as accountMenuAction /
// chatContextDispatch.
// ── SCOPE + CLOSE (D2 §5, P1) ─────────────────────────────────────────────
//
// MEASURED (D2, staging 1.33.1): with a workspace-module page open in the
// shell PageSlot, a right-click produced the HOME dashboard's widget menu —
// `Refresh · Add color · Hide "Team and agents" · Customize dashboard` — for a
// widget that was not even on screen; Escape did not close it, and it survived
// two page changes sitting over the module's own rail.
//
// Two independent shell defects, and both are fixed here:
//
// 1. THE HOME BOARD ANSWERS WHILE IT IS NOT THE SURFACE. The PageSlot frame is
//    inset (`left/right: X2`, `top: --slot-top`, `bottom: --slot-bottom`) and
//    `PageSlotBackdrop` is `pointerEvents: 'none'` by design, so every pixel
//    around the open pane is click-through to the dashboard behind it. A
//    right-click that misses the pane by a few px lands on a home widget and
//    opens ITS menu — which reads, correctly, as "the module page opened the
//    home menu". While a slot page is open the board is not the surface the
//    user is pointing at, so it must not answer at all.
//
// 2. ESCAPE ONLY WORKS IF FOCUS IS INSIDE THE MENU. The shared ContextDropdown
//    closes on Escape from `Menu.onKeydown`, a DELEGATED handler — smbls'
//    delegate walks the DOM ancestor chain of `event.target`
//    (smbls/packages/element/src/delegate.js installDelegatedEvent), so a
//    keystroke never reaches it unless focus actually moved into the panel.
//    `focusHomeContextMenu` below is that nudge, and it is a bounded poll that
//    can lose (a competing focus write, a menu that never paints). Escape is
//    the universal dismiss for a transient layer; it cannot depend on a race.
//    `closeContextMenuOnEscape` is the document-level backstop, wired on the
//    SHELL ROOT (app.js `onDocumentKeydown`) so it is armed on every route.
//
// A route change closes it too (app.js `onRouteChanged`): a menu is anchored
// to viewport coordinates on a surface that no longer exists.

import { focusContextMenu } from './contextMenuFocus.js'

// True when the home board owns this right-click. `false` while ANY PageSlot
// pane is open — see defect 1 above.
export const homeContextMenuAllowed = function (e, root) {
  const r = root || this?.state?.root
  if (!r) return false
  if (r._canvasSlot || r._canvasSlotSecond) return false
  // Text fields keep the native menu (the check every opener carried inline).
  const t = e && e.target
  if (t && t.closest && t.closest('input, textarea, [contenteditable]')) return false
  return true
}

// Document-level Escape — the backstop for defect 2. Also clears the home
// colour picker, the menu's own second step: Escape must dismiss the whole
// transient stack, not leave a palette floating with its menu gone.
export const closeContextMenuOnEscape = function (e) {
  if (!e || e.key !== 'Escape') return
  const root = this?.state?.root
  if (!root) return
  const patch = {}
  if (root.contextDropdown) patch.contextDropdown = null
  if (root._homeColorPicker) patch._homeColorPicker = null
  if (!Object.keys(patch).length) return
  // This Escape is spent: every later listener (the shortcut registry, the
  // customize drawer) yields to a handled key — the top overlay closes, alone.
  if (typeof e.preventDefault === 'function') e.preventDefault()
  root.update(patch, { preventFetch: true })
}

// Route change — same reasoning, different trigger. Called from the shell
// root's onRouteChanged, which already fires on every el.router() navigation.
export const closeContextMenuOnRouteChange = function () {
  const root = this?.state?.root
  if (!root || (!root.contextDropdown && !root._homeColorPicker)) return
  root.update({ contextDropdown: null, _homeColorPicker: null }, { preventFetch: true })
}

export const homeContextAction = function (action, item) {
  const root = this.state.root
  if (!root) return
  // Close the menu first — every branch here is terminal.
  root.update({ contextDropdown: null }, { preventFetch: true })
  const ops = root._homeOps || {}
  if (action === 'refresh') {
    // Refetch every dashboard widget's data in place — no page reload.
    if (typeof ops.refresh === 'function') ops.refresh()
  } else if (action === 'customize') {
    // Enter customize mode (slides in the CustomizeDrawer).
    root.update({ _homeCustomizeMode: true }, { preventFetch: true })
  } else if (action === 'widgetSize') {
    // A size row from the widget's menu on a touch screen
    // (functions/homeWidgetFrame.js homeWidgetSizeMenuRow) — the same durable
    // write as the edit-mode picker and the corner handle.
    if (item && item.widgetId && item.size && typeof ops.setWidgetSize === 'function') {
      ops.setWidgetSize(item.widgetId, item.size)
    }
  } else if (action === 'widgetColor') {
    // "Add color" / "Change color" — the SECOND step of a two-step menu
    // (2026-09-02, Nika: "add in context menu 'Add color' option so users can
    // customize widget colors + suggested colors").
    //
    // It opens `HomeWidgetColorPicker` (components/), a swatch palette, and
    // NOT a chained `root.contextDropdown`. The reason is measured, not
    // stylistic: a ContextDropdown row cannot carry a colour. Its ink is
    // `(el, s) => (s?.danger || s?.color === 'red' ? 'red' : 'title')` — a
    // two-value branch — and its only leading slots are an `Icon` painted by
    // that same ink and an `Avatar` fed a `src` URL. A palette of fourteen
    // suggested colours rendered as fourteen identically-black rows is not a
    // colour picker, and widening the shared component is a change in
    // packages/shared, which this ticket does not own.
    //
    // `item.x` / `item.y` are the cursor coords the OPENER stamped on the row
    // (pages/main.js, both `onContextmenu` sites): the dispatcher receives no
    // event, so the palette would otherwise open in a corner instead of where
    // the menu was.
    // `item.appHue` — the module's OWN manifest hue, when this widget has one.
    // It stopped painting by default on 2026-09-02 (Nika: "pipeline still has
    // color and set to no color also does not change it. fix it fundamentally
    // for also all app-store related apps and widgets too"), so it travels
    // here instead and the palette offers it as its first swatch. `null` on
    // the static plane, which hides that row.
    root.update(
      {
        _homeColorPicker: {
          widgetId: item && item.widgetId,
          x: (item && item.x) || 0,
          y: (item && item.y) || 0,
          appHue: (item && item.appHue) || null
        }
      },
      { preventFetch: true }
    )
  } else if (action === 'hideWidget') {
    // Hide the right-clicked widget (restorable from the customize drawer).
    if (typeof ops.hideWidget === 'function') ops.hideWidget(item && item.widgetId)
  } else if (action === 'shareAiWidget' || action === 'unshareAiWidget') {
    // AI-CREATED plane only — server re-checks ownership on every call
    // (viewerCanEdit is a STAMPED read of that same gate, never a client
    // decision; see functions/aiWidgets.js's "Manage mutations" header).
    if (typeof ops.setAiWidgetVisibility === 'function' && item && item.widget) {
      ops.setAiWidgetVisibility(item.widget, action === 'shareAiWidget' ? 'workspace' : 'personal')
    }
  } else if (action === 'deleteAiWidget') {
    // Asks first (CRUD standard rule 3, D1): the def is removed for good, and
    // a widget shared to the workspace goes for every member who sees it — the
    // message says so. Its visibility is read off the def row itself
    // (root.workspaceAiWidgets), the same row the delete then drops.
    if (typeof ops.deleteAiWidget === 'function' && item && item.widget) {
      const widget = item.widget
      const rows = Array.isArray(root.workspaceAiWidgets) ? root.workspaceAiWidgets : []
      const row = rows.find((w) => w && w.id === widget.id)
      const title = widget.title || this.call('polyglot', 'workspace.widget.untitled')
      this.call('confirmDialog', {
        title: this.call('polyglot', 'workspace.widget.deleteTitle').replace(
          '{title}',
          () => title
        ),
        // A personal widget takes the framework's own default message ("This
        // action cannot be undone.", en + ka).
        ...(row && row.visibility === 'workspace'
          ? { message: this.call('polyglot', 'workspace.widget.deleteSharedMessage') }
          : {}),
        confirmText: this.call('polyglot', 'common.action.delete'),
        cancelText: this.call('polyglot', 'common.action.cancel'),
        confirmTheme: 'primary',
        // `null` is the explicit opt-out; OMITTING the key takes the template's
        // 'warning' default.
        icon: null,
        onConfirm: () => ops.deleteAiWidget(widget)
      })
    }
  } else if (action === 'changeWidgetWithAi') {
    // "Change with AI…" (HOME-WIDGET-CHANGE-WITH-AI-1): the card rides to the
    // composer; the member says what to change, the assistant does it.
    this.call('homeWidgetChangeWithAi', item && item.widget)
  } else if (action === 'autolayout') {
    // Rearrange the board (GridStack .compact()) — the same primitive the
    // boot-time re-sync already calls, guarded there against overwriting a
    // saved arrangement; here IS that arrangement, by explicit request.
    if (typeof ops.autolayout === 'function') ops.autolayout()
  } else if (action === 'undoAutolayout') {
    if (typeof ops.undoAutolayout === 'function') ops.undoAutolayout()
  }
}

// INTERACT-1 (Home board only): focus the just-opened ContextDropdown's
// first row once it exists in the DOM. Every Home opener (empty-space +
// both per-widget onContextmenu handlers, pages/main.js) calls this right
// after writing `contextDropdown` — without it, DOM focus never moves off
// whatever had it before the right-click, so `shared/components/
// ContextDropdown.js`'s `Menu.onKeydown` (Escape, arrow-key nav) never
// receives a keystroke: DOMQL's keydown delegate walks the ANCESTOR chain
// of `event.target` (smbls/packages/element/src/delegate.js,
// installDelegatedEvent), and `event.target` on the next keypress is never
// a descendant of Menu's node if focus was never moved into it. This is
// the SAME root cause already independently confirmed twice on this
// ticket (tickets/sonnet.md INTERACT-1, workspace 99c1097a for the
// Assignee/Issuer/Epic filter Pickers; workspace a986e7ee for chat's
// context menu, reasoning from delegate.js directly) and documented on
// fable.md as a shared-component-level fix candidate — this is the SAME
// proven "move focus at the opener" technique (99c1097a's approach, not a
// shared-file edit), applied to Home's own openers.
//
// Bounded poll, not a single microtask check: the panel doesn't exist in
// the DOM until this render commits and the previously-`display:none`
// panel paints — same shape as `ContextDropdownItem.scope.openSubmenu`'s
// own `focusFirst` option polling for its submenu's first row.
//
// Scoped to `[data-key="ContextDropdown"]` (DOMQL stamps every element's
// OWN key as `data-key` — smbls/packages/element/src/create.js:531) rather
// than a bare `[role="menuitem"]` query: ContextDropdown is a single
// global singleton so this is normally unambiguous either way, but naming
// its subtree precisely means this can never accidentally grab a row from
// an unrelated open menu (e.g. an `@symbo.ls/dropdown` DropdownRoot panel,
// a completely different mechanism used elsewhere in this app).
//
// MOVED (WS-SHELL-CTXMENU-FOCUS-NEUTRAL-NAME-1, 2026-09-10). The body now
// lives in functions/contextMenuFocus.js under the neutral name
// `focusContextMenu`: the panel it focuses is the shell-wide
// `root.contextDropdown` singleton, not a Home-only affordance, and installed
// workspace MODULES already call it from their own pages. This export stays as
// a thin alias — same behaviour, same `this`, same arguments — so every caller
// that already wrote `el.call('focusHomeContextMenu')` (Home's three openers
// in pages/main.js, VaultOS 1.38.1, any other module) keeps working unchanged.
// A `function` (not an arrow) on purpose: `el.call('fn', ...)` runs
// `fn.call(element, ...)`, so the alias must forward `this` rather than close
// over its own lexical one.
export const focusHomeContextMenu = function (...args) {
  return focusContextMenu.apply(this, args)
}

// ── "Change with AI…" on a home card (HOME-WIDGET-CHANGE-WITH-AI-1) ────────
//
// Nika 2026-10-04: the apps' dashboard elements are home widgets "that can be
// regenerated with AI". The per-card menu row names the card for the
// assistant instead of asking the member to describe which one they mean.
//
// WHICH CARDS: an installed app's widget (it has a moduleId), a pinned app
// widget (an ai- row holding `<app>.<widget id>` by reference — homePinned
// HostRow stamps `pinnedFrom`), and an AI-made widget the member may edit
// (`viewerCanEdit`, server-stamped). A built-in shell card has no AI path, so
// it gets no row.
//
// `title` is the card's resolved title (the menu opener translates it).
// Pure over its input; returns the target or null.
export const homeWidgetAiTarget = function homeWidgetAiTarget(s, title) {
  if (!s || typeof s !== 'object' || !s.id) return null
  const name = String(title || s.title || s.id)
  if (typeof s.pinnedFrom === 'string' && s.pinnedFrom) {
    // A nested DOMQL state hands its plain values over through parse().
    const raw = s.params && typeof s.params === 'object' ? s.params : {}
    const params = typeof raw.parse === 'function' ? raw.parse() : { ...raw }
    return { card: s.id, ref: s.pinnedFrom, kind: 'pinned', title: name, params }
  }
  if (s.aiGenerated) {
    return s.viewerCanEdit ? { card: s.id, ref: s.id, kind: 'ai', title: name } : null
  }
  if (s.moduleId) {
    const key = String(s.moduleId).split('/').pop().replace(/-app$/, '')
    return { card: s.id, ref: key + '.' + s.id, kind: 'app', title: name }
  }
  return null
}

// The menu row's action: the card goes onto `root.pickedElements` — the same
// picked-context array the composer renders as a chip and that sendPrompt
// snapshots onto the next turn (TicketDetailModal's Ask AI, EventDetailModal);
// the transport prefixes `[user is pointing at: <key> — "<text>"]`, so the
// model gets the card id and the app ref while the member's words stay plain.
// `text` stays within the transport's 80-character snippet. Then the composer
// is prefilled ("Change “<title>”: ") and focused, cursor at the end — the
// send stays the member's (same steps as assistantBlockRefine). Asking twice
// about one card keeps one chip.
export const homeWidgetChangeWithAi = function homeWidgetChangeWithAi(target) {
  const t = target && typeof target === 'object' ? target : null
  const root = this && this.state && this.state.root
  if (!t || !t.card || !root || typeof root.update !== 'function') return false
  const title = String(t.title || t.card)
  const params = t.params && typeof t.params === 'object' ? t.params : {}
  const paramText = Object.keys(params).length
    ? ' · ' +
      Object.entries(params)
        .map(([k, v]) => k + '=' + (v && typeof v === 'object' ? JSON.stringify(v) : String(v)))
        .join(', ')
    : ''
  const refText = t.ref && t.ref !== t.card ? ' · widget ' + t.ref : ''
  const text = ('card ' + t.card + refText + paramText).slice(0, 80)
  const entry = {
    keyPath: ['Widget', title],
    key: String(this.call('polyglot', 'workspace.widget.changeWithAiKey')).replace(
      '{title}',
      title
    ),
    tag: 'widget',
    title,
    text,
    pickedAt: Date.now(),
    homeWidget: { card: t.card, ref: t.ref || t.card, kind: t.kind || 'app', params }
  }
  const prev = (Array.isArray(root.pickedElements) ? root.pickedElements : []).filter(
    (p) => !(p && p.homeWidget && p.homeWidget.card === t.card)
  )
  root.update({ pickedElements: [...prev, entry] })
  const draft = String(this.call('polyglot', 'workspace.widget.changeWithAiPrefix')).replace(
    '{title}',
    title
  )
  try {
    const top = typeof this.getRoot === 'function' ? this.getRoot() : null
    const composer = top && top.lookdown ? top.lookdown('CanvasPromptTextarea') : null
    if (composer && composer.state && typeof composer.state.update === 'function') {
      composer.state.update({ draft })
    }
    const field = composer && composer.lookdown ? composer.lookdown('Textarea') : null
    const node = field && field.node
    if (node && typeof node.focus === 'function') {
      node.focus()
      if (typeof node.setSelectionRange === 'function')
        node.setSelectionRange(draft.length, draft.length)
    }
  } catch (_) {
    /* the chip is attached either way; the member can type in the composer */
  }
  return true
}
